import { createSelector, createSlice } from '@reduxjs/toolkit'
import { fetchSession } from './userAPI'

import { fetchPagesAsync } from '../pages/pagesSlice'
import { fetchSharesAsync } from '../shares/sharesSlice'

const initialState = {
  isFetching: false,
  hasLoaded: false,
  didValidate: false,
  lastUpdated: null,
  currentUser: null,
  authToken: null,
  // OmniAuth's own request-phase CSRF token (session[:csrf], not Rails'
  // session[:_csrf_token]) -- see ApplicationController#set_csrf_token.
  // Kept fresh the same way authToken is, so OpenIdSignIn.jsx never
  // posts a stale one after a sign-out.
  omniauthToken: null,
}

export const fetchUserAsync = () => async (dispatch, getState) => {
  const json = await fetchSession(dispatch)
  dispatch(loadUserData(json))

  // If the user is logged in, there will be a record for them
  // in the json. We can also fetch their pages
  if (typeof (json) !== 'undefined'
    && typeof (json?.user) !== 'undefined'
    && json?.user !== null) {
    // Fetch the user's pages, and who they have shared them with
    await dispatch(fetchPagesAsync())
    await dispatch(fetchSharesAsync())
  }
}

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    loadUserData: (state, action) => {
      state.currentUser = action.payload?.user || null
      state.isFetching = false
      state.hasLoaded = true
      state.didValidate = true
      if (typeof action.payload?.auth_token !== 'undefined' && action.payload?.auth_token !== null) {
        state.authToken = action.payload.auth_token
      }
    },
    clearUserData: (state) => ({
      ...initialState,
      hasLoaded: true,
      didValidate: true,
      authToken: state.authToken,
      omniauthToken: state.omniauthToken,
    }),
    setAuthToken: (state, action) => {
      state.authToken = action.payload
    },
    setOmniauthToken: (state, action) => {
      state.omniauthToken = action.payload
    },
    // The server sends `last_page_id` once, with the user. Kept up to date as
    // pages of mine are opened, so going back to /app/pages lands where I
    // just was, as the server would say on the next load.
    setLastPageId: (state, action) => {
      if (state.currentUser) {
        state.currentUser.last_page_id = action.payload
      }
    },
  },
  extraReducers: (builder) => {
    // A deleted page can't be the one to go back to. Matched by type, since
    // pagesSlice imports from this file and importing `removePage` back
    // would be circular.
    builder.addCase('pages/removePage', (state, action) => {
      if (state.currentUser?.last_page_id === action.payload) {
        state.currentUser.last_page_id = null
      }
    })
  },
})

export const { loadUserData, clearUserData, setAuthToken, setOmniauthToken, setLastPageId } =
  userSlice.actions

export default userSlice.reducer

export const selectCurrentUser = (state) => state?.user?.currentUser

export const selectLastPageId = (state) => state?.user?.currentUser?.last_page_id

export const selectAuthToken = (state) => state?.user?.authToken

export const selectOmniauthToken = (state) => state?.user?.omniauthToken

export const userDidValidate = (state) => state.user.didValidate

